import { useRef, useState } from "react";
import { api } from "../lib/api";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { REVENUE_RANGES, BANK_OPTIONS, PARTICULARITIES, estimateSavings } from "../lib/savingsFormula";
import { SimProgress, NotedBanner } from "../components/simulator/SimUI";
import { StepRevenue, StepBanks, StepParticularities } from "../components/simulator/Steps";
import { ResultPanel } from "../components/simulator/ResultPanel";
import { SmallCompanyPanel } from "../components/simulator/SmallCompanyPanel";
import { TrustAside } from "../components/simulator/TrustAside";

const revenueLabel = (k) => REVENUE_RANGES.find((r) => r.key === k)?.label;
const bankOpt = (n) => BANK_OPTIONS.find((b) => b.n === n);

const REVENUE_NOTES = {
    "2-10": "Les PME payent souvent 70 % de plus que les ETI.",
    "10-50": "Les PME de ce scope de CA font en moyenne 55 % d'économie avec nous.",
    "50-100": "Les ETI sont la principale cible des banques. Pourquoi ? Vous rapportez beaucoup d'argent avec vos flux.",
    "100-250": "Une grosse ETI comme la vôtre doit payer des centaines de milliers d'euros par an de frais bancaires.",
    gt250: "Les Grandes Entreprises sont la cible parfaite des banques : des flux, des services bancaires de partout et des frais en bataille.",
};

const BANK_NOTES = {
    1: "Monobancaire. Attention au risque opérationnel et à l'abus de pouvoir de votre banque.",
    2: "Une diversification bancaire, mais une tarification rarement commune, même au sein du même groupement bancaire.",
    3: "De notre expérience, sur 3 banques, seules 2 sont réellement efficaces.",
    4: "Pas trop compliqué à gérer pour vous ? En tout cas, les frais bancaires doivent être multiples.",
};

const notedFor = (step, revenue, banks) => {
    if (step === 2 && revenue) return `Bien noté : ${revenueLabel(revenue)}. ${REVENUE_NOTES[revenue]}`;
    if (step === 3 && banks) return `Bien noté : ${bankOpt(banks).noted}. ${BANK_NOTES[banks]}`;
    return null;
};

function useLead() {
    const lead = useRef(null);
    const pending = useRef(null);
    const ensure = async () => {
        if (lead.current) return lead.current;
        if (!pending.current) pending.current = api.post("/leads", { source: "simulator-neo" }).then(({ data }) => (lead.current = data));
        return pending.current;
    };
    const headers = () => ({ headers: { "X-Lead-Token": lead.current.edit_token } });
    const patch = async (data) => {
        try {
            const l = await ensure();
            await api.patch(`/leads/${l.id}`, data, headers());
        } catch (e) {
            console.error(e);
        }
    };
    const post = async (path, body) => {
        const l = await ensure();
        return api.post(`/leads/${l.id}${path}`, body, headers());
    };
    return { patch, post };
}

export default function Simulateur() {
    useDocumentMeta({
        title: "Simulateur d'économies bancaires — FOD by DMB Partners",
        description: "Simulateur gratuit d'économies sur vos frais bancaires PME / ETI. Estimation instantanée en 3 questions : chiffre d'affaires, nombre de banques et particularités.",
        canonical: "https://www.fodbydmb.com/simulateur",
    });
    const [step, setStep] = useState(1); // 1 | 2 | 3 | "result" | "small"
    const [revenue, setRevenue] = useState(null);
    const [banks, setBanks] = useState(null);
    const [selected, setSelected] = useState({});
    const [none, setNone] = useState(false);
    const lead = useLead();
    const cardRef = useRef(null);

    const go = (s) => {
        setStep(s);
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };

    const pickRevenue = (k) => {
        setRevenue(k);
        lead.patch({ revenue_range: k, step_reached: 1 });
        go(k === "lt2" ? "small" : 2);
    };
    const pickBanks = (n) => {
        setBanks(n);
        lead.patch({ banks_count: n, step_reached: 2 });
        go(3);
    };
    const changeParts = (next, isNone) => {
        setSelected(next);
        setNone(isNone);
    };
    const particularitiesPayload = () =>
        Object.entries(selected).map(([key, r]) => ({ key, label: PARTICULARITIES.find((p) => p.key === key).label, ...(r === true ? {} : { range: r }) }));

    const estimate = revenue && banks ? estimateSavings(revenue, banks, selected) : null;

    const submit = () => {
        lead.patch({ particularities: particularitiesPayload(), estimated_savings: estimate.average, step_reached: 5 });
        go("result");
    };
    const restart = () => {
        setRevenue(null);
        setBanks(null);
        setSelected({});
        setNone(false);
        go(1);
    };

    const chips = estimate
        ? [`CA : ${revenueLabel(revenue)}`, bankOpt(banks).label === "4 ou plus" ? "4 banques ou plus" : bankOpt(banks).label, ...Object.keys(selected).map((k) => PARTICULARITIES.find((p) => p.key === k).label)]
        : [];

    return (
        <div data-testid="simulateur-page" className="mx-auto max-w-[1240px] px-4 md:px-8 lg:px-12 pt-8 md:pt-12 lg:pt-16 pb-12 md:pb-16 lg:pb-24 flex flex-wrap gap-x-12 gap-y-10 items-start">
            <div className="flex-[999_1_560px] min-w-0 flex flex-col gap-6">
                {step === 1 && (
                    <div className="flex flex-col gap-3 animate-fade-up">
                        <h1 className="m-0 font-bold text-[30px] md:text-[44px] lg:text-[52px] leading-[1.12] tracking-[-0.025em] text-navy">Combien payez-vous de trop à votre banque&nbsp;?</h1>
                        <p className="m-0 text-base md:text-lg text-grey-text">Estimation en 30 secondes. Anonyme et sans engagement.</p>
                    </div>
                )}
                <section ref={cardRef} aria-live="polite" data-testid="sim-card" className="bg-white rounded-2xl p-5 md:p-9 shadow-[0_20px_50px_-30px_rgba(15,27,45,0.35)] scroll-mt-6">
                    {typeof step === "number" && <SimProgress step={step} onBack={step > 1 ? () => go(step - 1) : null} />}
                    {typeof step === "number" && <NotedBanner>{notedFor(step, revenue, banks)}</NotedBanner>}
                    {step === 1 && <StepRevenue value={revenue} onSelect={pickRevenue} />}
                    {step === 2 && <StepBanks value={banks} onSelect={pickBanks} />}
                    {step === 3 && <StepParticularities selected={selected} none={none} onChange={changeParts} onSubmit={submit} />}
                    {step === "result" && estimate && (
                        <ResultPanel
                            estimate={estimate}
                            chips={chips}
                            onRdv={() => lead.post("/rdv-clicked", null).catch(() => {})}
                            onSendEmail={(email) => lead.post("/email-estimate", { email, consent: true })}
                            onRestart={restart}
                        />
                    )}
                    {step === "small" && <SmallCompanyPanel onEdit={() => go(1)} />}
                </section>
            </div>
            <TrustAside />
        </div>
    );
}
